import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatTRY, formatDate } from "@/lib/format";
import { toast } from "sonner";
import {
  ExternalLink, ClipboardPaste, Upload, Loader2, CheckCircle2, RefreshCw, Info, Save, Zap,
} from "lucide-react";

const ODEME_URL = "https://odeme.aytemiz.com.tr/";

export default function AytemizPanel({ onImported }) {
  const [cfg, setCfg] = useState({ vkn: "", musteri_kodu: "", last_import: null, last_count: 0 });
  const [text, setText] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const fileRef = useRef();

  const loadCfg = () => api.get("/aytemiz/config").then((r) => setCfg(r.data)).catch(() => {});
  useEffect(() => { loadCfg(); }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/aytemiz/sync");
      toast.success(`Otomatik çekim tamam: ${r.data.found} kayıt bulundu, ${r.data.imported} eklendi`);
      loadCfg();
      onImported?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Otomatik çekim başarısız");
    } finally { setSyncing(false); }
  };

  const saveCfg = async () => {
    setSaving(true);
    try {
      await api.post("/aytemiz/config", { vkn: cfg.vkn, musteri_kodu: cfg.musteri_kodu });
      toast.success("Bilgiler kaydedildi");
    } catch (e) { toast.error("Kaydedilemedi"); }
    finally { setSaving(false); }
  };

  const preview = async () => {
    if (!text.trim()) { toast.error("Önce tabloyu yapıştırın"); return; }
    setLoading(true);
    try {
      const r = await api.post("/aytemiz/parse", { text });
      setRows(r.data.rows);
      if (r.data.rows.length === 0) toast.error("Tarih/tutar bulunamadı. Tabloyu tekrar kopyalayın.");
    } catch (e) { toast.error("Ayrıştırılamadı"); }
    finally { setLoading(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/aytemiz/import-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRows(r.data.rows);
      toast.success(`${r.data.rows.length} satır bulundu`);
    } catch (e) { toast.error("Dosya okunamadı"); }
    finally { setLoading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const doImport = async () => {
    if (!rows || rows.length === 0) return;
    setLoading(true);
    try {
      const r = await api.post("/aytemiz/import", { rows });
      toast.success(`${r.data.imported} ödeme plana eklendi${r.data.skipped ? `, ${r.data.skipped} zaten mevcut` : ""}`);
      setRows(null); setText("");
      loadCfg();
      onImported?.();
    } catch (e) { toast.error("İçe aktarılamadı"); }
    finally { setLoading(false); }
  };

  const total = rows ? rows.reduce((s, r) => s + r.amount, 0) : 0;

  return (
    <div className="card-surface p-5 md:p-6 mb-6 fade-up" data-testid="aytemiz-panel">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E65100]/15 flex items-center justify-center">
            <RefreshCw className="w-5 h-5 text-[#E65100]" />
          </div>
          <div>
            <h3 className="font-display font-semibold">Aytemiz Ödeme Verisi</h3>
            <p className="text-xs text-[#94A3B8]">Vade tarihlerini ve tutarları ödeme planına aktarın</p>
          </div>
        </div>
        {cfg.last_import && (
          <div className="flex items-center gap-1.5 text-xs text-[#10B981]">
            <CheckCircle2 className="w-4 h-4" /> Son aktarım: {formatDate(cfg.last_import)} ({cfg.last_count} kayıt)
          </div>
        )}
      </div>

      {/* Step 1: identifiers */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end mb-5">
        <div>
          <label className="block text-xs text-[#94A3B8] mb-1.5">Vergi Kimlik No</label>
          <input data-testid="aytemiz-vkn-input" value={cfg.vkn} onChange={(e) => setCfg({ ...cfg, vkn: e.target.value })} className={inp} placeholder="5730042828" />
        </div>
        <div>
          <label className="block text-xs text-[#94A3B8] mb-1.5">Müşteri Kodu</label>
          <input data-testid="aytemiz-musteri-input" value={cfg.musteri_kodu} onChange={(e) => setCfg({ ...cfg, musteri_kodu: e.target.value })} className={inp} placeholder="100738" />
        </div>
        <button onClick={saveCfg} disabled={saving} data-testid="aytemiz-save-config" className="px-3 py-2.5 rounded-xl bg-[#161D2A] hover:bg-[#1D2637] border border-[#253043] text-sm font-medium flex items-center gap-2 transition-colors">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Kaydet
        </button>
        <a href={ODEME_URL} target="_blank" rel="noopener noreferrer" data-testid="aytemiz-open-page" className="px-3 py-2.5 rounded-xl bg-[#E65100] hover:bg-[#F57C00] text-white text-sm font-semibold flex items-center gap-2 transition-colors justify-center">
          <ExternalLink className="w-4 h-4" /> Aytemiz Sayfasını Aç
        </a>
      </div>

      {/* Auto sync (runs on your own TR server) */}
      <div className="flex flex-wrap items-center gap-3 mb-5 -mt-2">
        <button onClick={syncNow} disabled={syncing} data-testid="aytemiz-sync-button" className="px-4 py-2.5 rounded-xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-60">
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Otomatik Çek (Şimdi)
        </button>
        <span className="text-xs text-[#94A3B8]">
          {cfg.last_sync ? `Son otomatik çekim: ${formatDate(cfg.last_sync)} (${cfg.last_sync_found || 0} kayıt)` : "Sistem 12 saatte bir otomatik günceller (kendi sunucunuzda)"}
        </span>
      </div>

      {/* Step 2: paste */}
      <label className="block text-xs text-[#94A3B8] mb-1.5">Sayfanın alt kısmındaki vade/tutar tablosunu kopyalayıp buraya yapıştırın</label>
      <textarea
        data-testid="aytemiz-paste-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        className="w-full px-3 py-2.5 rounded-xl bg-[#111620] border border-[#253043] focus:border-[#E65100] outline-none transition-colors text-sm font-mono-num resize-y"
        placeholder="Örn:&#10;15.10.2026    1.403.200,00 ₺    Motorin İrsaliye&#10;22.10.2026    530.400,00 ₺    Benzin"
      />

      <div className="flex flex-wrap gap-3 mt-3">
        <button onClick={preview} disabled={loading} data-testid="aytemiz-preview-button" className="px-4 py-2.5 rounded-xl bg-[#3B82F6] hover:bg-[#2563EB] text-white text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardPaste className="w-4 h-4" />} Önizle
        </button>
        <button onClick={() => fileRef.current?.click()} className="px-4 py-2.5 rounded-xl bg-[#161D2A] hover:bg-[#1D2637] border border-[#253043] text-sm font-medium flex items-center gap-2 transition-colors">
          <Upload className="w-4 h-4" /> Excel/CSV Yükle
        </button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFile} className="hidden" data-testid="aytemiz-file-input" />
      </div>

      {/* Preview */}
      {rows && rows.length > 0 && (
        <div className="mt-4 rounded-xl border border-[#253043] overflow-hidden" data-testid="aytemiz-preview">
          <div className="flex items-center justify-between px-4 py-2.5 bg-[#111620] text-xs">
            <span className="text-[#94A3B8]">{rows.length} ödeme bulundu</span>
            <span className="font-mono-num font-semibold">Toplam: {formatTRY(total)}</span>
          </div>
          <div className="max-h-52 overflow-y-auto divide-y divide-[#253043]">
            {rows.map((r, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-2 text-sm">
                <span className="text-[#94A3B8]">{formatDate(r.due_date)}</span>
                <span className="truncate flex-1 px-3 text-xs text-[#94A3B8]">{r.desc}</span>
                <span className="font-mono-num font-medium">{formatTRY(r.amount)}</span>
              </div>
            ))}
          </div>
          <div className="p-3 bg-[#111620]">
            <button onClick={doImport} disabled={loading} data-testid="aytemiz-import-button" className="w-full py-2.5 rounded-xl bg-[#E65100] hover:bg-[#F57C00] text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Planı Oluştur ({rows.length} kayıt)
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 flex items-start gap-2.5 text-xs text-[#94A3B8] bg-[#111620] rounded-xl p-3">
        <Info className="w-4 h-4 text-[#3B82F6] shrink-0 mt-0.5" />
        <span>Tam otomatik çekim (<b>Otomatik Çek</b> butonu ve 12 saatlik zamanlayıcı) uygulamayı kendi Türkiye sunucunuzda çalıştırdığınızda devreye girer; Aytemiz bu sayfayı yalnızca kendi ağından açar. Her an manuel yol da kullanılabilir: <b>Aytemiz Sayfasını Aç</b> → tabloyu kopyala → yapıştır → <b>Önizle</b> → <b>Planı Oluştur</b>. Aynı kayıtlar tekrar eklenmez.</span>
      </div>
    </div>
  );
}

const inp = "w-full px-3 py-2.5 rounded-xl bg-[#111620] border border-[#253043] focus:border-[#E65100] outline-none transition-colors text-sm font-mono-num";
